import type { WorksheetAssignee } from '@mathflat/domain/Worksheet'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import Button from '@/component/atoms/Button'
import { Icon } from '@/component/atoms/Icon'
import { Loader } from '@/component/atoms/Loader'
import { StudentSelectContent } from '@/page/etc/MobileStudentSelect/MobileStudentSelect'

import { fetchQrScoringTarget, type QrScoringEntry } from '../_utils/fetchQrScoringTarget'
import { QR_REJECT_MESSAGE } from '../_utils/qrScoringTarget'
import type { WorksheetQrQuery } from '../_utils/worksheetQr'
import { openQrRejectModal } from '../QrRejectNotice'
import S from './QrRosterModal.style'

type Props = {
  query: WorksheetQrQuery
  /** 모달만 걷고 카메라로 돌아가 다시 찍는다 */
  onDismiss: () => void
  onPick: (assignee: WorksheetAssignee) => void
}

/** 태블릿에서 공통 QR 을 찍으면 카메라 위에 휴대폰 학생 선택과 같은 목록을 띄운다 */
const QrRosterModal = ({ query, onDismiss, onPick }: Props) => {
  const [entry, setEntry] = useState<QrScoringEntry | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    setEntry(null)
    void fetchQrScoringTarget(query).then((next) => {
      if (cancelled) return
      setEntry(next)
      if (next.type === 'reject') {
        openQrRejectModal({ message: QR_REJECT_MESSAGE[next.reason], onClose: onDismiss })
      }
    })
    return () => {
      cancelled = true
    }
  }, [query, reloadKey])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onDismiss])

  // 거절은 안내 창이 맡고, 닫으면 카메라로 돌아간다
  if (entry?.type === 'reject') return null

  const renderBody = () => {
    if (!entry) return <Loader />
    if (entry.type === 'error') {
      return (
        <div css={S.notice}>
          <b>학습지를 불러오지 못했어요</b>
          <button type="button" css={S.retry} onClick={() => setReloadKey((key) => key + 1)}>
            다시 시도
          </button>
        </div>
      )
    }
    return <StudentSelectContent worksheetId={query.worksheetId} onPick={onPick} />
  }

  return createPortal(
    <>
      <div css={S.dim} onClick={onDismiss} aria-hidden />
      <div css={S.modal} role="dialog" aria-label="학생 선택">
        <header css={S.head}>
          <b css={S.title}>학생 선택</b>
          <button type="button" css={S.close} onClick={onDismiss} aria-label="닫고 다시 스캔">
            <Icon name="close" size={24} color="var(--Colors-Gray-800)" />
          </button>
        </header>
        <div css={S.body}>{renderBody()}</div>
        <footer css={S.footer}>
          <Button size="l" width="full" leftIcon="qr_scan" onClick={onDismiss}>
            다음 QR 찍기
          </Button>
        </footer>
      </div>
    </>,
    document.body,
  )
}

export default QrRosterModal
